import { store } from '@/store';
import { fcmApi } from '@/store/api/fcmApi';
import { deleteFcmToken, getFcmToken } from '@/lib/firebase';
import {
  clearStoredFcmFid,
  getStoredFcmFid,
  isFcmPromptSnoozed,
  setStoredFcmFid,
} from '@/lib/fcmDevice';

/**
 * - unsupported: browser can't do web push (e.g. iOS Safari outside an installed PWA)
 * - default:     not answered yet — browser prompt, then the in-app fallback prompt
 * - denied:      blocked in browser settings; only the user can undo it there
 * - enabled:     permission granted and this device is registered with the backend
 * - disabled:    permission granted but not registered yet (or registration failed)
 */
export type PushStatus = 'unsupported' | 'default' | 'denied' | 'enabled' | 'disabled';

const listeners = new Set<() => void>();
let watchingPermission = false;
let autoPromptSettled = false;

function notify() {
  listeners.forEach((listener) => listener());
}

export function isPushSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

export function getPushStatus(): PushStatus {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'default';
  return getStoredFcmFid() ? 'enabled' : 'disabled';
}

/** For useSyncExternalStore; also picks up permission changes made in browser settings. */
export function subscribePushStatus(listener: () => void) {
  listeners.add(listener);

  if (!watchingPermission && isPushSupported() && navigator.permissions?.query) {
    watchingPermission = true;
    navigator.permissions
      .query({ name: 'notifications' })
      .then((permissionStatus) => {
        permissionStatus.onchange = notify;
      })
      .catch(() => {});
  }

  return () => {
    listeners.delete(listener);
  };
}

async function registerDevice(): Promise<boolean> {
  const token = await getFcmToken();
  if (!token) return false;

  const request = store.dispatch(fcmApi.endpoints.registerFcmDevice.initiate({ fid: token }));
  try {
    await request.unwrap();
    setStoredFcmFid(token);
    console.log('[FCM] device registered with backend');
    return true;
  } catch (error) {
    console.error('Failed to register FCM token', error);
    return false;
  } finally {
    request.reset();
    notify();
  }
}

/**
 * Registers this device with the backend without prompting — only when
 * permission is already granted.
 * Called after login / on load.
 */
let syncInFlight: Promise<boolean> | null = null;

export async function syncPushRegistration(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  // Dedupe concurrent calls (e.g. React StrictMode's double effect run).
  syncInFlight ??= registerDevice().finally(() => {
    syncInFlight = null;
  });
  await syncInFlight;
}

/** True once the automatic browser prompt after login has been answered, dismissed or skipped. */
export const hasAutoPromptSettled = () => autoPromptSettled;

let autoPromptInFlight: Promise<void> | null = null;

/**
 * Runs after login / on load: shows the browser's permission prompt if the user
 * hasn't answered it yet, then registers the device if permission is granted.
 * Firefox and Safari ignore prompts not triggered by a click (and Chrome may show
 * only a quiet icon), so if this leaves permission at "default",
 * PushNotificationPrompt shows an in-app fallback whose button can prompt.
 * Skipped while that fallback is snoozed, so dismissals don't get Chrome to
 * auto-block the site.
 */
export function autoRequestPushPermission(): Promise<void> {
  autoPromptInFlight ??= (async () => {
    if (!isPushSupported()) return;
    if (Notification.permission === 'default' && !isFcmPromptSnoozed()) {
      try {
        const permission = await Notification.requestPermission();
        console.log('[FCM] auto permission prompt result:', permission);
      } catch {
        // Some browsers reject instead of resolving when there's no user gesture.
      }
    }
    await syncPushRegistration();
  })().finally(() => {
    autoPromptSettled = true;
    autoPromptInFlight = null;
    notify();
  });
  return autoPromptInFlight;
}

/** Must be called from a click handler so the browser shows its permission prompt. */
export async function enablePushNotifications(): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    console.log('[FCM] permission prompt result:', permission);
  }
  if (Notification.permission === 'granted') {
    await registerDevice();
  }

  notify();
  return getPushStatus();
}

/**
 * Removes this device from the backend and drops its FCM token. Never throws,
 * and gives up waiting after `timeoutMs` so it can't block logout.
 */
export async function unregisterPushDevice(timeoutMs = 3000): Promise<void> {
  const fid = getStoredFcmFid();
  if (!fid) return;

  const request = store.dispatch(fcmApi.endpoints.deleteFcmDevice.initiate(fid));
  console.log('[FCM] unregistering device');
  await Promise.race([
    Promise.all([
      request.unwrap().catch((error) => console.error('Failed to unregister FCM device', error)),
      deleteFcmToken(),
    ]),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
  request.reset();

  clearStoredFcmFid();
  notify();
}
